import { PrimaryBluePalette } from '@tcl-boron-colors/colors';
import coreDesignTokens from '@tempus/core-design-foundation/tokens.js';
import { coreDesignTheme, mergeTheme } from '@tempus/core-design-theme-antd';
import { theme, type ThemeConfig } from 'antd';
import type { MappingAlgorithm } from 'antd/es/theme/interface';

import {
  boronDarkAlgorithm,
  getBoronComponentTokens,
  getBoronHighContrastToken,
  getBoronSeedToken,
} from './antdBoronTheme';

/**
 * Storybook Theme:
 * - disabled = Ant Design library defaults
 * - lightBoron / darkBoron = packaged TCL metrics merged over Core Design (Dark inverts neutrals)
 * - lightCarbon = `@tempus/core-design-theme-antd` seeds
 * - darkCarbon = those brand seeds on the flipped grayscale ramp (grayscale-1000 canvas, text-inverted)
 */
export type AntdAppearance = 'disabled' | 'lightBoron' | 'darkBoron' | 'lightCarbon' | 'darkCarbon';

export const ANTD_APPEARANCES: readonly AntdAppearance[] = [
  'disabled',
  'lightBoron',
  'darkBoron',
  'lightCarbon',
  'darkCarbon',
];

export const ANTD_APPEARANCE_LABELS: Record<AntdAppearance, string> = {
  disabled: 'Disabled',
  lightBoron: 'Light (Boron)',
  darkBoron: 'Dark (Boron)',
  lightCarbon: 'Light (Carbon)',
  darkCarbon: 'Dark (Carbon)',
};

/** Older toolbar values still stored in Storybook globals. */
const ANTD_APPEARANCE_ALIASES: Record<string, AntdAppearance> = {
  dark: 'darkCarbon',
  legacy: 'lightBoron',
  light: 'lightCarbon',
};

/** High contrast composes with every themed appearance. No-op when Theme is Disabled. */
export type AntdContrast = 'default' | 'high';

export const ANTD_CONTRASTS: readonly AntdContrast[] = ['default', 'high'];

export const ANTD_CONTRAST_LABELS: Record<AntdContrast, string> = {
  default: 'Default',
  high: 'High contrast',
};

export type AntdDirection = 'ltr' | 'rtl';

export const ANTD_DIRECTIONS: readonly AntdDirection[] = ['ltr', 'rtl'];

export const ANTD_DIRECTION_LABELS: Record<AntdDirection, string> = {
  ltr: 'LTR',
  rtl: 'RTL',
};

/**
 * CSS variable prefix for Ant Design tokens only (`--therapies-antd-color-primary`).
 * Do not reuse `--ant-*` so hashed antd styles cannot restyle Boron/TCL.
 */
export const ANTD_CSS_VAR_PREFIX = 'therapies-antd';

const APPEARANCE_CSS_KEYS: Record<Exclude<AntdAppearance, 'disabled'>, string> = {
  darkBoron: 'therapies-boron-dark',
  darkCarbon: 'therapies-carbon-dark',
  lightBoron: 'therapies-boron-light',
  lightCarbon: 'therapies-carbon-light',
};

export const isBoronAppearance = (appearance: AntdAppearance): appearance is 'lightBoron' | 'darkBoron' =>
  appearance === 'lightBoron' || appearance === 'darkBoron';

const getBoronMode = (appearance: 'lightBoron' | 'darkBoron'): 'light' | 'dark' =>
  appearance === 'darkBoron' ? 'dark' : 'light';

/**
 * Light page background is grayscale-100. Dark flips that ramp to grayscale-1000.
 * Body text flips from text-primary to text-inverted. Ant's dark algorithm then
 * raises container and elevated surfaces off that canvas.
 * Brand seeds stay the published hexes. The dark palette generator would shift them.
 */
const CARBON_DARK_BG = coreDesignTokens.color['grayscale-1000'];
const CARBON_DARK_TEXT = coreDesignTokens.color['text-inverted'];

/**
 * Light steps to PrimaryBlue 300, darker than the published primary.
 * Dark steps up the published ramp to blue-400 so the fill stays visible on grayscale-1000.
 */
const CARBON_HIGH_CONTRAST_PRIMARY: Record<'lightCarbon' | 'darkCarbon', string> = {
  darkCarbon: coreDesignTokens.color['blue-400'],
  lightCarbon: PrimaryBluePalette[300],
};

export const carbonDarkAlgorithm: MappingAlgorithm = (seed) => {
  const derived = theme.darkAlgorithm({
    ...seed,
    colorBgBase: CARBON_DARK_BG,
    colorTextBase: CARBON_DARK_TEXT,
  });

  return {
    ...derived,
    colorBgBase: CARBON_DARK_BG,
    // Pin the published seed strings, including case-sensitive CSS variables.
    colorError: seed.colorError,
    colorInfo: seed.colorInfo,
    colorLink: seed.colorLink,
    colorPrimary: seed.colorPrimary,
    colorSuccess: seed.colorSuccess,
    colorTextBase: CARBON_DARK_TEXT,
    colorWarning: seed.colorWarning,
  };
};

/** Thickens chrome. Primary comes from `CARBON_HIGH_CONTRAST_PRIMARY`; other Core Design seeds stay. */
const getCarbonHighContrastToken = (appearance: 'lightCarbon' | 'darkCarbon'): NonNullable<ThemeConfig['token']> => {
  const highContrastPrimary = CARBON_HIGH_CONTRAST_PRIMARY[appearance];

  return {
    colorInfo: highContrastPrimary,
    colorLink: highContrastPrimary,
    colorPrimary: highContrastPrimary,
    controlOutlineWidth: 3,
    lineWidth: 2,
    wireframe: true,
  };
};

/** Boron replaces overlapping seeds. Carbon default keeps `coreDesignTheme` and only overlays high contrast. */
const getAppearanceTokenOverride = (
  appearance: Exclude<AntdAppearance, 'disabled'>,
  contrast: AntdContrast,
): NonNullable<ThemeConfig['token']> | undefined => {
  if (isBoronAppearance(appearance)) {
    const mode = getBoronMode(appearance);
    return contrast === 'high' ? getBoronHighContrastToken(mode) : getBoronSeedToken(mode);
  }

  return contrast === 'high' ? getCarbonHighContrastToken(appearance) : undefined;
};

export const isAntdAppearance = (value: unknown): value is AntdAppearance =>
  typeof value === 'string' && (ANTD_APPEARANCES as readonly string[]).includes(value);

export const resolveAntdAppearance = (value: unknown): AntdAppearance => {
  if (isAntdAppearance(value)) {
    return value;
  }

  if (typeof value === 'string') {
    const aliased = ANTD_APPEARANCE_ALIASES[value];
    if (aliased) {
      return aliased;
    }
  }

  return 'lightCarbon';
};

export const isAntdContrast = (value: unknown): value is AntdContrast =>
  typeof value === 'string' && (ANTD_CONTRASTS as readonly string[]).includes(value);

export const resolveAntdContrast = (value: unknown): AntdContrast => (isAntdContrast(value) ? value : 'default');

export const isAntdDirection = (value: unknown): value is AntdDirection =>
  typeof value === 'string' && (ANTD_DIRECTIONS as readonly string[]).includes(value);

export const resolveAntdDirection = (value: unknown): AntdDirection => (isAntdDirection(value) ? value : 'ltr');

export const getAntdCssVarKey = (appearance: Exclude<AntdAppearance, 'disabled'>, contrast: AntdContrast): string =>
  contrast === 'high' ? `${APPEARANCE_CSS_KEYS[appearance]}-high-contrast` : APPEARANCE_CSS_KEYS[appearance];

export const getAntdThemeConfig = (
  appearance: AntdAppearance,
  contrast: AntdContrast = 'default',
): ThemeConfig | undefined => {
  if (appearance === 'disabled') {
    return undefined;
  }

  const algorithm =
    appearance === 'darkBoron'
      ? boronDarkAlgorithm
      : appearance === 'darkCarbon'
        ? carbonDarkAlgorithm
        : theme.defaultAlgorithm;

  const token = getAppearanceTokenOverride(appearance, contrast);
  const components = isBoronAppearance(appearance) ? getBoronComponentTokens(getBoronMode(appearance)) : undefined;

  // TcdConfigProvider merges `coreDesignTheme` again. Returning the full theme keeps that second merge idempotent.
  return mergeTheme(coreDesignTheme, {
    algorithm,
    cssVar: { key: getAntdCssVarKey(appearance, contrast), prefix: ANTD_CSS_VAR_PREFIX },
    hashed: true,
    ...(components ? { components } : {}),
    ...(token ? { token } : {}),
  });
};
